import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agentService } from "../../services/api";
import { ProposedAction } from "../ai/ChatActionPlan";

export default function RecordProposals({ run, busy, inputKey, planId }) {
  const [status, setStatus] = useState("pending");
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState("");
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["record-proposals", status, offset, inputKey || "all", planId || "all"],
    queryFn: () => agentService.recordProposals({ status, offset, limit: 20, ...(inputKey ? {input_key: inputKey} : {}), ...(planId ? {plan_id: planId} : {}) }),
    refetchInterval: 15000,
  });
  const changeStatus = (value) => {
    setStatus(value);
    setOffset(0);
    setNotice("");
  };
  return (
    <section
      className="space-y-3"
      aria-label="Record updates from your information"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold">
            {planId ? "Plan updates from linked sources" : "Record updates from your information"}
          </h2>
          <p className="mt-1 text-xs leading-5 text-[#68746d]">
            {planId ? "Linked notes, monitored documents and analysed Gmail conversations can propose changes to a step. Review the evidence and exact changes before saving. Linking an email does not enable monitoring." : "Review task, plan step, contact, occasion, vehicle and routine changes found in your sources. Actual progress is recorded only after you accept it."}
          </p>
        </div>
        <label className="text-xs font-semibold text-[#58635d]">
          Show record updates
          <select
            className="ml-2 rounded-md border border-[#dce3dd] bg-white p-2"
            value={status}
            onChange={(event) => changeStatus(event.target.value)}
          >
            <option value="pending">To review</option>
            <option value="accepted">Applied</option>
            <option value="dismissed">Dismissed</option>
            <option value="superseded">Replaced / needs recheck</option>
          </select>
        </label>
      </div>
      {notice && (
        <p role="status" className="surface-panel p-3 text-sm text-[#246b52]">
          {notice}
        </p>
      )}
      {isLoading && <p role="status">Loading record updates…</p>}
      {error && (
        <div className="surface-panel p-4">
          <p role="alert">Record updates could not load.</p>
          <button className="secondary-button mt-2" onClick={() => refetch()}>
            Retry record updates
          </button>
        </div>
      )}
      {data && (
        <>
          <p className="text-xs text-[#758078]">
            {data.total}{" "}
            {status === "pending"
              ? "to review"
              : status === "accepted"
                ? "applied"
                : status}
          </p>
          {!data.items.length && (
            <p className="surface-panel p-5 text-sm text-[#68746d]">
              {status === "pending"
              ? "No record changes waiting for review. Analyse the selected source to find supported updates. Missing dates or matching records appear as clarification questions in Agent activity."
                : "No record updates in this view."}
            </p>
          )}
          {data.items.map((proposal) => (
            <article key={proposal.id} className="surface-panel space-y-3 p-4">
              <div className="flex flex-wrap justify-between gap-2 text-xs text-[#68746d]">
                <span>
                  From{" "}
                  {/^https?:\/\//i.test(proposal.source_url || "") ? (
                    <a
                      href={proposal.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {proposal.source_title}
                    </a>
                  ) : (
                    proposal.source_title
                  )}
                </span>
                <span className="status-pill">
                  {proposal.status === "accepted"
                    ? "Applied"
                    : proposal.stale_reason
                      ? "Needs recheck"
                      : proposal.status === "pending"
                        ? "Not applied"
                        : proposal.status}
                </span>
              </div>
              <ProposedAction
                action={proposal.command}
                timezone={proposal.command.timezone || data.timezone}
                evidenceLabel="Source evidence"
              />
              {proposal.receipt && (
                <p className="text-sm text-[#246b52]">
                  Saved: {proposal.receipt.title}.{" "}
                  <a
                    className="font-semibold underline"
                    href={proposal.receipt.link}
                  >
                    {proposal.command.operation === "plan.step_reconcile" ? "Open plan" : proposal.command.operation === "task.reconcile" ? "Open task workspace" : "Open record workspace"}
                  </a>
                  {proposal.applied_at && <span className="mt-1 block text-xs text-[#68746d]">Applied {new Date(proposal.applied_at).toLocaleString(undefined, { timeZone: data.timezone || "UTC" })} ({data.timezone || "UTC"})</span>}
                </p>
              )}
              {proposal.stale_reason && (
                <p role="status" className="text-sm text-[#996617]">
                  {proposal.stale_reason}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {proposal.status === "pending" && (
                  <button
                    className="primary-button text-xs"
                    disabled={busy || !!proposal.stale_reason}
                    onClick={() =>
                      run(async () => {
                        await agentService.decideRecord(proposal.id, "accept");
                        setNotice(
                          proposal.command.operation === "plan.step_reconcile"
                            ? "Plan step updated. Progress, reminders and saved history now reflect the accepted change."
                            : proposal.command.operation === "task.reconcile"
                            ? "Task update saved. The same tracked task and its update history are available in Applied."
                            : "Record change saved. The updated record and receipt are available in Applied.",
                        );
                      })
                    }
                  >
                    {proposal.command.operation === "plan.step_reconcile" ? "Apply plan step update" : proposal.command.operation === "task.reconcile" ? "Apply task update" : "Apply record change"}
                  </button>
                )}
                {["pending", "superseded"].includes(proposal.status) && (
                  <button
                    className="secondary-button text-xs"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await agentService.decideRecord(proposal.id, "dismiss");
                        setNotice("Record change dismissed.");
                      })
                    }
                  >
                    Dismiss record change
                  </button>
                )}
                {proposal.can_recheck && (
                  <button
                    className="quiet-button text-xs"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const result = await agentService.recheckRecord(
                          proposal.id,
                        );
                        setNotice(result.message);
                      })
                    }
                  >
                    Recheck source
                  </button>
                )}
              </div>
            </article>
          ))}
          <div className="flex items-center gap-3 text-xs">
            {offset > 0 && (
              <button
                className="secondary-button"
                onClick={() => setOffset(Math.max(0, offset - 20))}
              >
                Previous record updates
              </button>
            )}
            {data.next_offset !== null && (
              <button
                className="secondary-button"
                onClick={() => setOffset(data.next_offset)}
              >
                More record updates
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
